import * as React from 'react';
import {
  Map as LeafletMap,
  TileLayer,
  Marker as OSMMarker,
  withLeaflet,
} from 'react-leaflet';
import { divIcon } from 'leaflet';
import clsx from 'clsx';
import { renderToStaticMarkup } from 'react-dom/server';
import { IconMarkerPractitioner } from '@maiia/design-system';
import 'leaflet/dist/leaflet.css';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { analyticsEvent } from '@/src/analytic_tag_manager';
import { capitalizeFirstLetter } from '@/src/utils';
import PharmaciesLayer from './Pharmacies';
import { mapServerUrlSelector } from '@/src/selectors';

type MarkerProps = {
  id: any;
  publicInformation: any;
  markerRef: any;
  onClick: any;
  highlight: any;
};

type Props = {
  items: Array<any>;
  useSearchContext?: boolean;
  highlightItemId: string;
  setHighlightItemId: Function;
  onViewportChange: Function;
  goToMarker: Function;
  onItemSelected: Function;
  viewport: {
    longitude: number;
    latitude: number;
    zoom: number;
  };
  showPharmacies?: boolean;
  enableTrackEvents?: boolean;
};

const mapPathName = {
  '/[speciality]/[locality]': 'SearchPage',
  '/[speciality]/[locality]/[slug]': 'PractitionerPage',
};

const MapTracker = ({ leaflet }) => {
  const router = useRouter();
  React.useEffect(() => {
    const handler = (e: any) => {
      analyticsEvent({
        category: 'MapEvents',
        action: `Map${capitalizeFirstLetter(e.type)}`,
        label: mapPathName?.[router.pathname] ?? router.pathname,
      });
    };
    leaflet.map.on('zoomend move', handler);
    return () => {
      leaflet.map.off('zoomend move', handler);
    };
  }, []);
  return null;
};

const MapTrackerMemorized = withLeaflet(React.memo(MapTracker));

const Marker = (props: MarkerProps) => {
  const { id, publicInformation, markerRef, onClick, highlight } = props;
  if (!publicInformation?.address?.location) return null;
  const customIcon = divIcon({
    html: renderToStaticMarkup(
      <IconMarkerPractitioner
        className={clsx(highlight && 'highlight', 'map-marker')}
        onClick={onClick}
      />,
    ),
  });
  return (
    <OSMMarker
      ref={markerRef}
      key={id}
      position={{
        lat: publicInformation?.address?.location?.y,
        lng: publicInformation?.address?.location?.x,
      }}
      icon={customIcon}
      onClick={onClick}
    />
  );
};

const OpenStreetMap = ({
  items,
  useSearchContext,
  highlightItemId,
  setHighlightItemId,
  onViewportChange,
  goToMarker,
  onItemSelected,
  viewport,
  showPharmacies,
  enableTrackEvents,
}: Props) => {
  const router = useRouter();
  const onTileLayerLoaded = React.useMemo(() => {
    if (enableTrackEvents) {
      analyticsEvent({
        category: 'MapEvents',
        action: 'MapLoaded',
        label: mapPathName?.[router.pathname] ?? router.pathname,
      });
    }
  }, []);

  const addressServerUrl = useSelector(mapServerUrlSelector);

  if (!viewport?.longitude || !viewport?.latitude) {
    return null;
  }
  const leafletViewport = {
    center: [viewport.latitude, viewport.longitude],
    zoom: viewport.zoom,
  };
  const _onViewportChange = ({ center: [latitude, longitude], zoom }) => {
    onViewportChange({
      longitude,
      latitude,
      zoom,
    });
  };

  if (!addressServerUrl) {
    return null;
  }

  return (
    <LeafletMap viewport={leafletViewport} onViewportChange={_onViewportChange}>
      <TileLayer
        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
        url={addressServerUrl}
        onLoad={onTileLayerLoaded}
      />
      {items.map(item => {
        const { ref: itemRef, ...props } = item;
        return (
          <Marker
            key={item.id}
            highlight={useSearchContext ? highlightItemId === item.id : false}
            {...props}
            onClick={() => {
              if (itemRef && itemRef.current) goToMarker(itemRef);
              if (onItemSelected) onItemSelected(item);
              if (useSearchContext) setHighlightItemId(item.id);
              if (enableTrackEvents) {
                analyticsEvent({
                  category: 'MapEvents',
                  action: 'MapMarkerClick',
                  label: mapPathName?.[router.pathname] ?? router.pathname,
                });
              }
            }}
          />
        );
      })}
      {showPharmacies && <PharmaciesLayer />}
      {enableTrackEvents && <MapTrackerMemorized />}
    </LeafletMap>
  );
};

export default OpenStreetMap;
